/** Smallest on-screen cell the grid still draws, in pixels. */
export const MIN_GRID_SPACING = 10;
/** Cell size at which the grid is fully opaque, in pixels. */
const FULL_OPACITY_SPACING = 20;
/** How far the grid coarsens (in doublings of the base size) before it
 *  gives up and disappears. */
const MAX_DOUBLINGS = 3;

/** From this zoom on the grid is drawn over the shapes (as the Konva
 *  editor does), so it stays usable for fine placement on top of walls
 *  and furniture. Below it, it sits under them like paper. */
export const GRID_OVER_SHAPES_SCALE = 1.5;

export interface GridAppearance {
  /** World-unit distance between drawn lines. */
  step: number;
  opacity: number;
  /** The drawn step is coarser than the document grid. */
  isCoarsened: boolean;
  /** Drawn above the document layer instead of under it. */
  isOverShapes: boolean;
}

/**
 * Grid step and opacity for a zoom level. Zooming out doubles the step
 * while cells would fall under `MIN_GRID_SPACING` on screen (up to
 * `MAX_DOUBLINGS`), so the grid stays readable instead of turning into a
 * grey wash; further out it fades and then disappears. Zoomed in, the
 * document grid is drawn as is.
 */
export const gridAppearance = (
  gridSize: number,
  scale: number
): GridAppearance | null => {
  if (gridSize <= 0 || scale <= 0) return null;
  let step = gridSize;
  let doublings = 0;
  while (step * scale < MIN_GRID_SPACING && doublings < MAX_DOUBLINGS) {
    step *= 2;
    doublings += 1;
  }
  const spacing = step * scale;
  if (spacing < MIN_GRID_SPACING) return null;
  const opacity = Math.min(
    1,
    Math.max(
      0.25,
      (spacing - MIN_GRID_SPACING) / (FULL_OPACITY_SPACING - MIN_GRID_SPACING)
    )
  );
  return {
    step,
    opacity,
    isCoarsened: doublings > 0,
    isOverShapes: scale >= GRID_OVER_SHAPES_SCALE
  };
};

/**
 * Live update of the grid while the wheel zooms: the viewport only reaches
 * the store when the wheel settles, so the pattern is patched in place
 * here in the meantime. Moving the grid between the layers waits for the
 * commit; only step and opacity change live.
 */
export const applyGridToDom = (
  svg: SVGSVGElement,
  gridSize: number,
  scale: number
): void => {
  const pattern = svg.querySelector<SVGPatternElement>("[data-grid-pattern]");
  const rect = svg.querySelector<SVGRectElement>("[data-grid-step]");
  if (!pattern || !rect) return;
  const grid = gridAppearance(gridSize, scale);
  if (!grid) {
    rect.setAttribute("opacity", "0");
    return;
  }
  pattern.setAttribute("width", String(grid.step));
  pattern.setAttribute("height", String(grid.step));
  pattern
    .querySelector("path")
    ?.setAttribute("d", `M ${grid.step} 0 L 0 0 0 ${grid.step}`);
  rect.setAttribute("opacity", String(grid.opacity));
  rect.setAttribute("data-grid-step", String(grid.step));
};
